// src/components/Dashboard/charts/MultireportAndContractCharts.tsx
import React from "react";
import { Tabs } from "antd";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Empty } from "antd";

const { TabPane } = Tabs;

interface MultireportAndContractChartsProps {
  multireportData: any[];
  contractData: any[];
}

const MultireportAndContractCharts: React.FC<
  MultireportAndContractChartsProps
> = ({ multireportData, contractData }) => {
  // Format the multireport data
  const formatData = (data: any[], userField: string) => {
    if (!data || data.length === 0) {
      return [];
    }

    return data.map((item) => {
      const firstName = item[`${userField}__first_name`] || "";
      const lastName = item[`${userField}__last_name`] || "";
      const email = item[`${userField}__email`] || "";

      // Create display name
      let displayName = email;
      displayName = email.split("@")[0];
      if (firstName || lastName) {
        displayName = `${firstName} ${lastName}`.trim();
        // Keep it short
        if (displayName.length > 15) {
          displayName = `${displayName.substring(0, 15)}...`;
        }
      }

      return {
        name: displayName,
        count: item.count,
      };
    });
  };

  const formattedMultireportData = formatData(multireportData, "assigned_user");
  const formattedContractData = formatData(contractData, "collaborator");

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-md rounded-md">
          <p className="font-medium text-gray-600">{label}</p>
          <p className="text-purple-600">
            <span className="font-bold">{payload[0].value}</span>{" "}
            {payload[0].name.toLowerCase()}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h2 className="text-lg font-medium mb-4 text-gray-700">
        Reportes and Contratos
      </h2>
      <Tabs defaultActiveKey="1">
        <TabPane tab="Multireportes Entregados por Usuario" key="1">
          <div className="h-64">
            {formattedMultireportData.length === 0 ? (
              <div className="flex justify-center items-center h-full">
                <Empty description="No data available" />
              </div>
            ) : (
              <ResponsiveContainer>
                <BarChart
                  data={formattedMultireportData}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 30,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: "#6b7280", fontSize: 12 }}
                    tickLine={{ stroke: "#e5e7eb" }}
                    axisLine={{ stroke: "#e5e7eb" }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    tick={{ fill: "#6b7280" }}
                    tickLine={{ stroke: "#e5e7eb" }}
                    axisLine={{ stroke: "#e5e7eb" }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="count"
                    name="Multireportes Entregados"
                    fill="#8b5cf6"
                    radius={[4, 4, 0, 0]}
                    className="hover:opacity-80 transition-opacity duration-300"
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </TabPane>
        <TabPane tab="Contratos Generados por Usuario" key="2">
          <div className="h-64">
            {formattedContractData.length === 0 ? (
              <div className="flex justify-center items-center h-full">
                <Empty description="No data available" />
              </div>
            ) : (
              <ResponsiveContainer>
                <BarChart
                  data={formattedContractData}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 30,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: "#6b7280", fontSize: 12 }}
                    tickLine={{ stroke: "#e5e7eb" }}
                    axisLine={{ stroke: "#e5e7eb" }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    tick={{ fill: "#6b7280" }}
                    tickLine={{ stroke: "#e5e7eb" }}
                    axisLine={{ stroke: "#e5e7eb" }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="count"
                    name="Contratos Generados"
                    fill="#ec4899"
                    radius={[4, 4, 0, 0]}
                    className="hover:opacity-80 transition-opacity duration-300"
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default MultireportAndContractCharts;
